import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Only log errors in development
if (import.meta.env.DEV && (!supabaseUrl || !supabaseKey)) {
  console.error('Supabase environment variables are missing:', {
    url: supabaseUrl ? 'defined' : 'missing',
    key: supabaseKey ? 'defined' : 'missing'
  });
}

// Create a single instance of the Supabase client to be used throughout the app.
// Placeholder fallbacks keep the app rendering when env vars are absent
// (createClient throws on undefined) - the calendar works entirely offline;
// only auth/saving require a real Supabase project.
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseKey || 'placeholder-anon-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    },
    global: {
      headers: {
        'X-Client-Info': 'supabase-js-web/2.38.4',
      },
    },
    db: {
      schema: 'public'
    }
  }
);

// Add a simple test function to check Supabase connectivity (development only)
export const testSupabaseConnection = async () => {
  // Only run in development
  if (!import.meta.env.DEV) {
    return true;
  }

  try {
    if (import.meta.env.DEV) {
      console.log('Testing connection to Supabase URL:', supabaseUrl);
    }
    
    // Add timeout to the request
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Connection timeout')), 10000)
    );
    
    // Changed from 'profiles' to 'schedules' since profiles doesn't exist
    const connectionPromise = supabase.from('schedules').select('count', { count: 'exact' }).limit(1);
    
    // Race between the connection attempt and the timeout
    const { error } = await Promise.race([connectionPromise, timeoutPromise]) as { data?: unknown; error?: { message: string; code?: string; details?: string; hint?: string } };
    
    if (error) {
      if (import.meta.env.DEV) {
        console.error('Supabase connection test failed:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
      }
      return false;
    }
    
    if (import.meta.env.DEV) {
      console.log('Supabase connection successful');
    }
    return true;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('Supabase connection test exception:', err);
      if (err instanceof Error) {
        console.error('Exception details:', {
          message: err.message,
          name: err.name,
          stack: err.stack
        });
      }
    }
    return false;
  }
}; 